/**
 * ClinicalSavePanel — shown below a clinical AI answer in the bubble.
 * Lets the pharmacist pick a patient from a dropdown and save the Q&A
 * as an AI clinical note (status: pending_review) on that patient's record.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ChevronDown, Save, X, CheckCircle, Loader2, FlaskConical } from 'lucide-react';
import { Button } from '../ui/button';

import { authFetch } from '../../lib/api';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClinicalSavePanel = ({ question, answer, drugs = [], onSaved }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const timerRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await authFetch(`${API_URL}/api/patients/autocomplete?q=${encodeURIComponent(q)}&limit=6`, { credentials: 'include' });
      const data = await r.json();
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(timerRef.current);
  }, [query, search]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API_URL}/api/duti/clinical-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          patient_id: selected.patient_id,
          patient_name: selected.name || '',
          question,
          answer,
          drugs,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not save note');
      }
      const note = await r.json();
      setSaved(true);
      toast.success(`Saved to ${selected.name}'s record — awaiting your review`);
      if (onSaved) onSaved(note);
    } catch (e) {
      toast.error(e.message || 'Could not save clinical note');
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400" data-testid="clinical-save-done">
        <CheckCircle className="h-3.5 w-3.5 shrink-0" />
        Saved to {selected?.name}'s record — awaiting pharmacist review
      </div>
    );
  }

  return (
    <div className="mt-2 border-t pt-2" data-testid="clinical-save-panel">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[11px] text-primary hover:underline font-medium"
          data-testid="clinical-save-open-btn"
        >
          <FlaskConical className="h-3 w-3" />
          Save to patient record
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-foreground">Which patient?</span>
            <button type="button" onClick={() => { setOpen(false); setSelected(null); setQuery(''); setResults([]); }}
              className="text-muted-foreground hover:text-foreground" data-testid="clinical-save-close-btn">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {selected ? (
            <div className="flex items-center justify-between rounded border px-2 py-1.5 bg-muted/40 text-xs">
              <span className="font-medium" data-testid="clinical-save-selected">{selected.name}</span>
              <button type="button" onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground ml-2">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search patient name or phone…"
                className="w-full rounded border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary pr-6"
                data-testid="clinical-save-search-input"
              />
              {searching && <Loader2 className="absolute right-1.5 top-2 h-3 w-3 animate-spin text-muted-foreground" />}
              {!searching && <ChevronDown className="absolute right-1.5 top-2 h-3 w-3 text-muted-foreground" />}
              {results.length > 0 && (
                <ul className="absolute z-50 mt-1 w-full rounded border bg-popover shadow-md text-xs max-h-40 overflow-y-auto">
                  {results.map((p) => (
                    <li key={p.patient_id}>
                      <button
                        type="button"
                        onClick={() => { setSelected(p); setResults([]); setQuery(''); }}
                        className="w-full text-left px-2.5 py-1.5 hover:bg-muted flex flex-col"
                        data-testid={`clinical-save-patient-${p.patient_id}`}
                      >
                        <span className="font-medium">{p.name}</span>
                        {p.phone && <span className="text-muted-foreground text-[10px]">{p.phone}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {query.length >= 2 && !searching && results.length === 0 && (
                <p className="absolute z-50 mt-1 w-full rounded border bg-popover shadow-md text-xs px-2.5 py-1.5 text-muted-foreground">
                  No patients found
                </p>
              )}
            </div>
          )}

          {drugs.length > 0 && (
            <p className="text-[10px] text-muted-foreground">
              Drugs: {drugs.join(', ')}
            </p>
          )}

          <Button
            size="sm"
            className="h-7 text-[11px] w-full"
            disabled={!selected || saving}
            onClick={handleSave}
            data-testid="clinical-save-confirm-btn"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
            {saving ? 'Saving…' : `Save to ${selected?.name || 'patient'}'s record`}
          </Button>
        </div>
      )}
    </div>
  );
};
